import { Box, Typography } from "@mui/material";
import React from "react";
import { HorizontalBox } from "src/components/common/box/HorizontalBox.tsx";
import { VerticalBox } from "src/components/common/box/VerticalBox.tsx";
import { LabelValueView, LabelValueViewItem } from "src/components/common/misc/LabelValueView.tsx";

export interface ObjectInfoViewProps {
	title: string;
	/** Short identifying details shown under the title, such as the object number and state. */
	subtitleItems?: Array<React.ReactNode | false>;
	/** Actions shown next to the title, such as edit and more buttons. */
	actions?: React.ReactNode;
	/** The most important values of the object, highlighted in the header. */
	keyFacts?: ObjectInfoKeyFact[];
	/** Remaining details, shown below the header. */
	items: LabelValueViewItem[];
}

export interface ObjectInfoKeyFact {
	label: string;
	value: string | null | undefined;
}

/**
 * Info panel of a single object view: a header with the object's identity, actions and key facts,
 * followed by a scrollable list of the remaining details.
 */
export function ObjectInfoView({ title, subtitleItems = [], actions, keyFacts = [], items }: ObjectInfoViewProps) {
	const visibleSubtitleItems = subtitleItems.filter((item) => item !== false && item != null);
	return (
		<VerticalBox
			sx={{
				flex: 1,
				overflow: "hidden",
			}}
		>
			<VerticalBox
				sx={{
					gap: 1.5,
					padding: 1,
					paddingBottom: 1.5,
					borderBottom: 1,
					borderColor: "divider",
				}}
			>
				<HorizontalBox
					sx={{
						alignItems: "flex-start",
						gap: 1,
					}}
				>
					<VerticalBox sx={{ flex: 1 }}>
						<Typography variant={"h5"} sx={{ overflowWrap: "anywhere" }}>
							{title}
						</Typography>
						{visibleSubtitleItems.length > 0 && (
							<HorizontalBox
								sx={{
									alignItems: "center",
									flexWrap: "wrap",
									columnGap: 1.5,
									rowGap: 0.5,
									color: "text.secondary",
								}}
							>
								{visibleSubtitleItems.map((item, index) =>
									typeof item === "string" || typeof item === "number" ?
										<Typography key={index} variant={"body2"}>
											{item}
										</Typography>
									:	<React.Fragment key={index}>{item}</React.Fragment>,
								)}
							</HorizontalBox>
						)}
					</VerticalBox>
					{actions && <HorizontalBox sx={{ gap: 0.5, flexShrink: 0 }}>{actions}</HorizontalBox>}
				</HorizontalBox>
				{keyFacts.length > 0 && (
					<Box
						sx={{
							display: "grid",
							gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))",
							gap: 1.5,
						}}
					>
						{keyFacts.map((fact) => (
							<VerticalBox key={fact.label}>
								<Typography variant={"caption"} sx={{ color: "text.secondary" }}>
									{fact.label}
								</Typography>
								<Typography variant={"body1"} sx={{ fontWeight: "bold", overflowWrap: "anywhere" }}>
									{fact.value ?? "–"}
								</Typography>
							</VerticalBox>
						))}
					</Box>
				)}
			</VerticalBox>
			<VerticalBox
				sx={{
					flex: 1,
					overflow: "auto",
				}}
			>
				<LabelValueView
					sx={{
						overflow: "visible",
					}}
					fontSize={"small"}
					items={items}
				/>
			</VerticalBox>
		</VerticalBox>
	);
}
